import { useState, useEffect } from 'react';
import { authFetch } from '../../../api';

interface StorageConfig {
  provider?: 'local' | 's3' | 'gcs';
  s3_bucket?: string; s3_region?: string; s3_access_key?: string; s3_secret_key?: string; s3_endpoint?: string;
  gcs_bucket?: string; gcs_project_id?: string; gcs_key_json?: string;
}

const PROVIDERS = [
  { id: 'local', label: 'Local disk', icon: '💾', desc: 'Files stored on the server filesystem. Good for development.', color: '#F3F4F6', text: '#374151' },
  { id: 's3', label: 'AWS S3', icon: '🟠', desc: 'Amazon S3 object storage. Scalable, durable, CDN-ready.', color: '#FFF7ED', text: '#C2410C' },
  { id: 'gcs', label: 'Google Cloud Storage', icon: '🔵', desc: 'GCS object storage. Integrates with Google ecosystem.', color: '#EFF6FF', text: '#1D4ED8' },
] as const;

export default function StorageSettingsTab() {
  const [cfg, setCfg] = useState<StorageConfig>({});
  const [saving, setSave] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTest] = useState(false);
  const [testResult, setTR] = useState<{ ok: boolean; provider?: string; error?: string } | null>(null);
  const [showSecrets, setShow] = useState(false);

  useEffect(() => {
    authFetch('/api/storage-config').then((r: Response) => r.json()).then(setCfg).catch(() => {});
  }, []);

  const f = (k: keyof StorageConfig, v: string) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSave(true); setTR(null);
    await authFetch('/api/storage-config', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cfg) });
    setSave(false); setSaved(true); setTimeout(() => setSaved(false), 2200);
  };

  const test = async () => {
    await save(); setTest(true); setTR(null);
    const res = await authFetch('/api/storage-config/test', { method: 'POST' });
    const data = await res.json();
    setTR(data); setTest(false);
  };

  return (
    <div className="fade-in" style={{ maxWidth: 680 }}>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>Storage provider</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
          {PROVIDERS.map((p) => (
            <div key={p.id} onClick={() => f('provider', p.id)}
              style={{ padding: '14px 12px', border: `2px solid ${cfg.provider === p.id ? p.text : '#E5E7EB'}`, background: cfg.provider === p.id ? p.color : '#fff', borderRadius: 'var(--radius)', cursor: 'pointer', textAlign: 'center', transition: 'all .15s' }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>{p.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: cfg.provider === p.id ? p.text : '#111827', marginBottom: 3 }}>{p.label}</div>
              <div style={{ fontSize: 11, color: '#9CA3AF', lineHeight: 1.4 }}>{p.desc}</div>
              {cfg.provider === p.id && <div style={{ marginTop: 6, fontSize: 10, fontWeight: 700, color: p.text }}>ACTIVE</div>}
            </div>
          ))}
        </div>
      </div>

      {cfg.provider === 's3' && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>AWS S3 configuration</div>
          <div className="grid2" style={{ marginBottom: 14 }}>
            <div><label className="lbl">S3 Bucket name</label><input value={cfg.s3_bucket || ''} onChange={(e) => f('s3_bucket', e.target.value)} placeholder="my-hms-bucket" /></div>
            <div><label className="lbl">Region</label><input value={cfg.s3_region || 'ap-south-1'} onChange={(e) => f('s3_region', e.target.value)} /></div>
            <div><label className="lbl">Access key ID</label><input value={cfg.s3_access_key || ''} onChange={(e) => f('s3_access_key', e.target.value)} placeholder="AKIA…" /></div>
            <div><label className="lbl">Secret access key</label>
              <div style={{ position: 'relative' }}>
                <input type={showSecrets ? 'text' : 'password'} value={cfg.s3_secret_key || ''} onChange={(e) => f('s3_secret_key', e.target.value)} />
                <button onClick={() => setShow((p) => !p)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', fontSize: 11, color: '#6B7280', cursor: 'pointer' }}>{showSecrets ? 'Hide' : 'Show'}</button>
              </div>
            </div>
            <div style={{ gridColumn: '1/-1' }}><label className="lbl">Custom endpoint (optional — for S3-compatible storage like MinIO)</label><input value={cfg.s3_endpoint || ''} onChange={(e) => f('s3_endpoint', e.target.value)} placeholder="https://…" /></div>
          </div>
        </div>
      )}

      {cfg.provider === 'gcs' && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14 }}>Google Cloud Storage configuration</div>
          <div className="grid2" style={{ marginBottom: 14 }}>
            <div><label className="lbl">GCS Bucket name</label><input value={cfg.gcs_bucket || ''} onChange={(e) => f('gcs_bucket', e.target.value)} placeholder="hms-documents" /></div>
            <div><label className="lbl">GCP Project ID</label><input value={cfg.gcs_project_id || ''} onChange={(e) => f('gcs_project_id', e.target.value)} placeholder="my-project-123456" /></div>
            <div style={{ gridColumn: '1/-1' }}>
              <label className="lbl">Service account key (JSON)</label>
              <textarea style={{ height: 80, fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }} value={cfg.gcs_key_json === '[configured]' ? '' : cfg.gcs_key_json || ''} onChange={(e) => f('gcs_key_json', e.target.value)} placeholder='{"type":"service_account","project_id":"…"}' />
              {cfg.gcs_key_json === '[configured]' && <div style={{ fontSize: 11, color: '#065F46', marginTop: 4 }}>✓ Service account key is configured. Leave blank to keep existing.</div>}
            </div>
          </div>
        </div>
      )}

      {cfg.provider === 'local' && (
        <div style={{ padding: '12px 14px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 'var(--radius)', fontSize: 13, color: '#92400E', marginBottom: 16 }}>
          ⚠ Local disk storage is not recommended for production. Files are stored at <code style={{ fontFamily: 'monospace', background: '#FEF3C7', padding: '1px 5px', borderRadius: 3 }}>backend/uploads/</code>. Switch to S3 or GCS for reliable document storage.
        </div>
      )}

      {testResult && (
        <div style={{ padding: '10px 14px', borderRadius: 'var(--radius)', marginBottom: 16, background: testResult.ok ? '#ECFDF5' : '#FEF2F2', border: `1px solid ${testResult.ok ? '#A7F3D0' : '#FECACA'}`, fontSize: 13, color: testResult.ok ? '#065F46' : '#991B1B', fontWeight: 500 }}>
          {testResult.ok ? `✓ Connection successful — ${testResult.provider} is working correctly` : `✗ Connection failed: ${testResult.error}`}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn-primary" onClick={save} disabled={saving}>{saved ? '✓ Saved!' : saving ? 'Saving…' : 'Save settings'}</button>
        <button className="btn-ghost" onClick={test} disabled={testing}>{testing ? 'Testing…' : '⚡ Test connection'}</button>
      </div>
    </div>
  );
}
